import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { cardClient } from "@/api";
import { logger } from "@/shared/common/utils/logger.util";
import {
  isCompanyComplete,
  isCompanyCompleteWarning,
  isCompanyBlocked,
  isCompanyIncomplete,
  isCompanyPending,
  isUserRejected,
  isCompanyRejected,
  isUnlockedRouteKyc,
  isUserIncomplete,
  isUserVerifiedPhone,
  isUserProcess,
  isUserRequestedPartner,
  isOnboardingCompleted,
} from "@/helpers/kyc";
import { isGlobal81 } from "@/helpers/wallet";
import type { KycCompliance } from "@/types/KycCompliance";
import { isPaymentCallbackRoute } from "./isPaymentCallbackRoute";


function pushRedirectToOnboardingInvitation(kycStatus: KycCompliance) {
  return {
    name: 'onboarding-invitation',
    query: {
      update: isUserVerifiedPhone(kycStatus.b2bUser) ? 'level' : undefined,
    },
  };
}

export const verifyKycStatus = (
  to: RouteLocationNormalized,
  kycStatus: KycCompliance
): null | RouteLocationRaw => {
  if (isCompanyBlocked(kycStatus) && to.name !== 'movements') {
    return { name: 'movements' };
  }

  const isOnboardingInvitationRoute = to.name === 'onboarding-invitation';

  if (isUserRequestedPartner(kycStatus)) {
    if (isOnboardingInvitationRoute) return null;
    return pushRedirectToOnboardingInvitation(kycStatus);
  }

  const isCompanyOrUserInTransition =
    isCompanyIncomplete(kycStatus) ||
    isCompanyPending(kycStatus) ||
    isCompanyRejected(kycStatus) ||
    isUserRejected(kycStatus?.b2bUser) ||
    isUserProcess(kycStatus?.b2bUser);

  if (isCompanyOrUserInTransition) {
    if (
      to.name !== 'movements' &&
      !isUnlockedRouteKyc(to.name?.toString()) &&
      !isPaymentCallbackRoute(to)
    ) {
      return { name: 'movements' };
    }
    return null;
  }

  const shouldRedirectByIncompleteUser =
    (isCompanyComplete(kycStatus.company) ||
      isCompanyCompleteWarning(kycStatus.company)) &&
    isUserIncomplete(kycStatus.b2bUser);

  if (shouldRedirectByIncompleteUser) {
    if (isOnboardingInvitationRoute) return null;
    return pushRedirectToOnboardingInvitation(kycStatus);
  }

  if (
    isOnboardingCompleted(kycStatus) &&
    !isUserVerifiedPhone(kycStatus.b2bUser) &&
    to.name !== 'verify-phone-otp' &&
    !isOnboardingInvitationRoute
  ) {
    return { name: 'verify-phone-otp' };
  }

  return null;
};

// Extraer la lógica de verificación de wallets a una función separada
export const verifyWalletAndCardTerms = async (
  storeAccount, 
  storeWallet, 
  to: RouteLocationNormalized, 
  from: RouteLocationNormalized
): Promise<null | RouteLocationRaw> => {
  if (storeAccount?.kycStatus?.company?.addressCountry?.country !== 'CL') {
    return null;
  }

  if (!storeWallet.wallets.length) {
    await storeWallet.setWallets();
  }

  if (!storeWallet.getMainWallet.isMainAccount) {
    return null;
  }

  const isCard = !isGlobal81(storeWallet.getMainWallet, 'CLP');
  if (isCard) {
    return null;
  }

  try {
    const { data } = await cardClient.checkTermsAndConditions();
    const hasTermsCard = data?.value || false;

    if (!hasTermsCard) {
      const allowedRoutes = ['movements', 'card', 'verify-phone-otp', 'shareholders-declaration', 'shareholders-declaration-colombia', 'jumio-regularization-confirm', 'jumio-regularization-pending'];
      if (!allowedRoutes.includes(String(to.name)) && !isPaymentCallbackRoute(to)) {
        return from.name ? { name: String(from.name) } : { name: 'movements' };
      }
    }
  } catch (err) {
    logger.error(
      'Error al verificar términos de tarjeta',
      { routeName: String(to.name) },
      err as Error
    );
  }

  return null;
};

export const validateOnboardingAccess = (
  to: RouteLocationNormalized, 
  kycStatus: KycCompliance
): null | RouteLocationRaw => {
  if (to.name == 'onboarding-index' && isCompanyComplete(kycStatus.company)) {
    return { name: 'movements' };
  }

  return null;
};
